import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, Clock, CheckCircle, AlertCircle, Shield, FileText, Wrench, Home } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const EinbruchschadenReparaturPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  return (
    <>
      <Helmet>
        <title>Einbruchschaden Reparatur Dortmund – Tür & Fenster Notfall-Reparatur nach Einbruch</title>
        <meta name="description" content="Einbruch? Wir reparieren beschädigte Türen & Fenster sofort & professionell in Dortmund. Schadensdokumentation für Versicherung inklusive. 24/7 Notdienst: 0231 39085116." />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/einbruchschaden-reparatur/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs 
          items={[
            { label: 'Leistungen', link: '/leistungen' },
            { label: 'Einbruchschaden Reparatur' }
          ]} 
        />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Einbruchschaden Reparatur in Dortmund – Tür & Fenster schnell wieder sicher
        </h1>

        {/* Einleitung */}
        <div className="max-w-4xl mb-12">
          <p className="text-xl text-gray-800 font-semibold mb-4 flex items-start gap-2">
            <AlertCircle className="text-red-600 mt-1 flex-shrink-0" size={24} />
            <span>Opfer eines Einbruchs geworden? Wir sind sofort für Sie da!</span>
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Ein Einbruch ist nicht nur ein materieller Schaden – er hinterlässt oft auch ein Gefühl der Unsicherheit. 
            Beschädigte Türen und Fenster müssen schnell repariert werden, damit Ihr Zuhause wieder sicher ist und Sie 
            sich wieder wohlfühlen können.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Als professioneller Schlüsseldienst in Dortmund bieten wir Ihnen schnelle und fachgerechte Einbruchschaden-Reparatur 
            an Türen und Fenstern. Wir dokumentieren den Schaden für Ihre Versicherung, sichern Ihr Objekt provisorisch ab und 
            führen die dauerhafte Reparatur durch – alles aus einer Hand.
          </p>
          <p className="text-lg text-gray-700">
            Unsere Schössow Dienstleistungen UG ist 24 Stunden am Tag, 7 Tage die Woche für Sie erreichbar. 
            Wir kommen schnell zu Ihnen nach Hause – auch nachts, am Wochenende oder an Feiertagen – und stellen 
            Ihre Sicherheit wieder her.
          </p>
        </div>

        {/* Notfall-CTA */}
        <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-orange-400 rounded-xl p-6 mb-12 shadow-md">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Einbruch? Sofortiger Notruf!</h3>
              <p className="text-gray-700">Wir sind in 15-30 Minuten bei Ihnen und sichern Ihr Objekt ab.</p>
            </div>
            <a 
              href={`tel:${phone}`}
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg hover:shadow-xl flex items-center gap-2 whitespace-nowrap"
            >
              <Phone size={24} />
              {displayPhone}
            </a>
          </div>
        </div>

        {/* Vorteile */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
            <CheckCircle className="text-green-600" size={32} />
            Ihre Vorteile bei Einbruchschaden-Reparatur
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-orange-400 transition-all">
              <div className="flex items-start gap-4">
                <Clock className="text-orange-600 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">24/7 Notdienst</h3>
                  <p className="text-gray-700">
                    Nach einem Einbruch zählt jede Minute. Wir sind rund um die Uhr erreichbar und kommen 
                    schnell zu Ihnen – auch nachts und am Wochenende.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-orange-400 transition-all">
              <div className="flex items-start gap-4">
                <FileText className="text-orange-600 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">Schadensdokumentation für Versicherung</h3>
                  <p className="text-gray-700">
                    Wir dokumentieren den Schaden professionell mit Fotos und detaillierter Beschreibung. 
                    Sie erhalten alle Unterlagen für Ihre Versicherung.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-orange-400 transition-all">
              <div className="flex items-start gap-4">
                <Shield className="text-orange-600 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">Provisorische & dauerhafte Sicherung</h3>
                  <p className="text-gray-700">
                    Zunächst sichern wir Ihr Objekt provisorisch ab (z.B. mit Brettern oder Notschlössern). 
                    Anschließend erfolgt die fachgerechte Reparatur.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-orange-400 transition-all">
              <div className="flex items-start gap-4">
                <Wrench className="text-orange-600 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">Fachgerechte Tür- & Fensterreparatur</h3>
                  <p className="text-gray-700">
                    Wir reparieren aufgebrochene Türen, kaputte Schlösser, beschädigte Zargen und 
                    eingeschlagene Fenster professionell und dauerhaft.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-orange-400 transition-all">
              <div className="flex items-start gap-4">
                <Home className="text-orange-600 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">Sicherheitsberatung inklusive</h3>
                  <p className="text-gray-700">
                    Nach der Reparatur beraten wir Sie kostenlos, wie Sie Ihr Zuhause besser gegen 
                    zukünftige Einbrüche schützen können.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:border-orange-400 transition-all">
              <div className="flex items-start gap-4">
                <CheckCircle className="text-orange-600 flex-shrink-0 mt-1" size={28} />
                <div>
                  <h3 className="text-xl font-bold mb-2 text-gray-900">Transparente Preise</h3>
                  <p className="text-gray-700">
                    Sie erhalten vorab einen verbindlichen Kostenvoranschlag. Die Kosten können 
                    in der Regel über Ihre Hausratversicherung abgerechnet werden.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Ablauf */}
        <section className="mb-12 bg-gray-50 rounded-xl p-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            So läuft die Einbruchschaden-Reparatur ab
          </h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Notruf & Anfahrt (15-30 Min.)</h3>
                <p className="text-gray-700">
                  Sie rufen uns an unter <a href={`tel:${phone}`} className="text-orange-600 font-semibold hover:underline">{displayPhone}</a>. 
                  Wir machen uns sofort auf den Weg zu Ihnen – meist sind wir innerhalb von 15 bis 30 Minuten vor Ort in Dortmund.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Schadensbegutachtung & Dokumentation</h3>
                <p className="text-gray-700">
                  Wir begutachten den Schaden an Türen und Fenstern und dokumentieren alles mit Fotos und 
                  einer detaillierten Beschreibung. Diese Unterlagen benötigen Sie für Ihre Versicherung und ggf. die Polizei.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Provisorische Sicherung</h3>
                <p className="text-gray-700">
                  Damit Sie und Ihr Eigentum sofort wieder geschützt sind, sichern wir aufgebrochene Türen 
                  und eingeschlagene Fenster provisorisch ab. Das kann z.B. durch ein Notschloss, Bretter oder Folie erfolgen.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Kostenvoranschlag & Versicherung</h3>
                <p className="text-gray-700">
                  Sie erhalten einen transparenten Kostenvoranschlag für die Reparatur. In den meisten Fällen 
                  übernimmt Ihre Hausratversicherung die Kosten für die Schadensbeseitigung – wir unterstützen Sie gerne bei der Abwicklung.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                5
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Fachgerechte Reparatur von Tür & Fenster</h3>
                <p className="text-gray-700">
                  Wir führen die dauerhafte Reparatur durch: Türschloss wechseln, Türzarge ausbessern, 
                  Scheiben ersetzen, Fensterrahmen reparieren. Alles wird fachgerecht und sauber ausgeführt.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-xl">
                6
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Sicherheitsberatung & Abschluss</h3>
                <p className="text-gray-700">
                  Nach der Reparatur beraten wir Sie gerne, wie Sie Ihr Zuhause besser schützen können – 
                  z.B. durch Sicherheitsbeschläge, Zusatzschlösser oder abschließbare Fenstergriffe. Ihre Sicherheit liegt uns am Herzen.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Welche Schäden wir reparieren */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            Diese Einbruchschäden reparieren wir
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white border border-gray-300 rounded-lg p-4 hover:shadow-md transition-all">
              <h3 className="font-bold text-gray-900 mb-2">✓ Aufgebrochene Türschlösser</h3>
              <p className="text-gray-600 text-sm">Austausch defekter Schließzylinder und Schlösser</p>
            </div>
            <div className="bg-white border border-gray-300 rounded-lg p-4 hover:shadow-md transition-all">
              <h3 className="font-bold text-gray-900 mb-2">✓ Beschädigte Türzargen</h3>
              <p className="text-gray-600 text-sm">Ausbessern oder Austausch von Türrahmen</p>
            </div>
            <div className="bg-white border border-gray-300 rounded-lg p-4 hover:shadow-md transition-all">
              <h3 className="font-bold text-gray-900 mb-2">✓ Eingetretene Türen</h3>
              <p className="text-gray-600 text-sm">Türblatt-Reparatur oder Austausch</p>
            </div>
            <div className="bg-white border border-gray-300 rounded-lg p-4 hover:shadow-md transition-all">
              <h3 className="font-bold text-gray-900 mb-2">✓ Eingeschlagene Fenster</h3>
              <p className="text-gray-600 text-sm">Scheibenaustausch und Rahmenreparatur</p>
            </div>
            <div className="bg-white border border-gray-300 rounded-lg p-4 hover:shadow-md transition-all">
              <h3 className="font-bold text-gray-900 mb-2">✓ Aufgehebelte Fenster</h3>
              <p className="text-gray-600 text-sm">Reparatur von Fensterrahmen und Beschlägen</p>
            </div>
            <div className="bg-white border border-gray-300 rounded-lg p-4 hover:shadow-md transition-all">
              <h3 className="font-bold text-gray-900 mb-2">✓ Defekte Sicherungstechnik</h3>
              <p className="text-gray-600 text-sm">Austausch beschädigter Sicherheitsbeschläge</p>
            </div>
          </div>
        </section>

        {/* Versicherung */}
        <section className="mb-12 bg-blue-50 border-2 border-blue-200 rounded-xl p-8">
          <h2 className="text-3xl font-bold mb-6 text-gray-900 flex items-center gap-3">
            <FileText className="text-blue-600" size={32} />
            Versicherung & Kostenübernahme
          </h2>
          
          <div className="space-y-4">
            <p className="text-lg text-gray-700">
              <strong>Gute Nachricht:</strong> Die Kosten für die Einbruchschaden-Reparatur werden in den meisten Fällen 
              von Ihrer <strong>Hausratversicherung</strong> übernommen. Dazu gehören:
            </p>
            
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Notöffnung und provisorische Sicherung</li>
              <li>Reparatur oder Austausch von Türen und Schlössern</li>
              <li>Reparatur oder Austausch von Fenstern und Scheiben</li>
              <li>Ausbessern beschädigter Türzargen oder Fensterrahmen</li>
            </ul>

            <p className="text-lg text-gray-700 mt-4">
              <strong>So gehen Sie vor:</strong>
            </p>
            <ol className="list-decimal list-inside space-y-2 text-gray-700 ml-4">
              <li>Einbruch bei der Polizei anzeigen (wichtig für Versicherung!)</li>
              <li>Uns anrufen für die Schadensbehebung</li>
              <li>Wir dokumentieren den Schaden mit Fotos und Beschreibung</li>
              <li>Sie erhalten alle Unterlagen für Ihre Versicherung</li>
              <li>Versicherung prüft und übernimmt in der Regel die Kosten</li>
            </ol>

            <div className="bg-white rounded-lg p-4 mt-6 border border-blue-300">
              <p className="text-gray-700">
                <strong>💡 Tipp:</strong> Bewahren Sie alle Rechnungen und Belege auf. Wir stellen Ihnen 
                eine detaillierte Rechnung aus, die Sie direkt bei Ihrer Versicherung einreichen können.
              </p>
            </div>
          </div>
        </section>

        {/* Preise */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            Preise für Einbruchschaden-Reparatur
          </h2>
          
          <div className="bg-white border-2 border-orange-200 rounded-xl p-6">
            <p className="text-lg text-gray-700 mb-4">
              Die Kosten für die Einbruchschaden-Reparatur hängen vom Umfang des Schadens ab. 
              Hier einige Richtwerte:
            </p>
            
            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-gray-700">Provisorische Sicherung (Notdienst)</span>
                <span className="font-bold text-gray-900">ab 150 €</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-gray-700">Türschloss-Austausch</span>
                <span className="font-bold text-gray-900">ab 100 €</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-gray-700">Türzarge ausbessern/ersetzen</span>
                <span className="font-bold text-gray-900">ab 200 €</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-gray-700">Fensterscheibe ersetzen</span>
                <span className="font-bold text-gray-900">ab 150 €</span>
              </div>
              <div className="flex justify-between items-center border-b border-gray-200 pb-2">
                <span className="text-gray-700">Schadensdokumentation</span>
                <span className="font-bold text-gray-900">inklusive</span>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4">
              * Alle Preise inkl. Anfahrt innerhalb Dortmund. Die genauen Kosten hängen vom konkreten Schadensfall ab. 
              Sie erhalten vorab einen verbindlichen Kostenvoranschlag.
            </p>

            <div className="bg-green-50 border border-green-300 rounded-lg p-4">
              <p className="text-gray-700">
                ✓ In den meisten Fällen übernimmt Ihre Hausratversicherung die Kosten<br/>
                ✓ Transparente Abrechnung mit detaillierter Rechnung<br/>
                ✓ Keine versteckten Kosten
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">
            Häufige Fragen zur Einbruchschaden-Reparatur
          </h2>
          
          <div className="space-y-4">
            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Was sollte ich direkt nach einem Einbruch tun?
              </h3>
              <p className="text-gray-700">
                1. Berühren Sie nichts am Tatort (Spurensicherung!)<br/>
                2. Rufen Sie die Polizei (110)<br/>
                3. Machen Sie Fotos vom Schaden<br/>
                4. Rufen Sie uns an für die provisorische Sicherung<br/>
                5. Melden Sie den Schaden Ihrer Versicherung
              </p>
            </div>

            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Übernimmt die Versicherung die Reparaturkosten?
              </h3>
              <p className="text-gray-700">
                Ja, in der Regel übernimmt die Hausratversicherung die Kosten für die Schadensbehebung nach einem Einbruch. 
                Wichtig ist, dass Sie den Einbruch bei der Polizei anzeigen und den Schaden Ihrer Versicherung melden. 
                Wir liefern Ihnen alle nötigen Unterlagen für die Versicherung.
              </p>
            </div>

            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Wie schnell können Sie nach einem Einbruch vor Ort sein?
              </h3>
              <p className="text-gray-700">
                Wir sind in der Regel innerhalb von 15 bis 30 Minuten bei Ihnen in Dortmund. 
                Unser Notdienst ist 24 Stunden am Tag, 7 Tage die Woche erreichbar – auch nachts, 
                am Wochenende und an Feiertagen.
              </p>
            </div>

            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Was bedeutet "provisorische Sicherung"?
              </h3>
              <p className="text-gray-700">
                Damit Ihr Zuhause nach dem Einbruch sofort wieder geschützt ist, sichern wir beschädigte 
                Türen und Fenster zunächst provisorisch ab. Das kann durch ein Notschloss, Bretter vor Fenstern 
                oder spezielle Folien erfolgen. Anschließend erfolgt die dauerhafte, fachgerechte Reparatur.
              </p>
            </div>

            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Können Sie auch die Tür komplett austauschen?
              </h3>
              <p className="text-gray-700">
                Ja, wenn die Tür zu stark beschädigt ist und eine Reparatur nicht mehr sinnvoll oder möglich ist, 
                können wir auch einen kompletten Türaustausch durchführen. Wir beraten Sie vor Ort und erstellen 
                Ihnen ein Angebot für eine neue, sichere Tür.
              </p>
            </div>

            <div className="bg-white border border-gray-300 rounded-lg p-6">
              <h3 className="text-xl font-bold mb-3 text-gray-900">
                Bieten Sie auch Einbruchschutz-Beratung an?
              </h3>
              <p className="text-gray-700">
                Ja, nach der Reparatur beraten wir Sie gerne kostenlos, wie Sie Ihr Zuhause besser gegen 
                zukünftige Einbrüche schützen können. Wir zeigen Ihnen verschiedene mechanische Sicherungslösungen 
                wie Sicherheitsbeschläge, Zusatzschlösser oder abschließbare Fenstergriffe. 
                Mehr Infos finden Sie auf unserer Seite <Link to="/leistungen/einbruchschutz" className="text-orange-600 font-semibold hover:underline">Einbruchschutz</Link>.
              </p>
            </div>
          </div>
        </section>

        {/* Abschluss-CTA */}
        <section className="bg-gradient-to-r from-orange-500 to-amber-400 text-white rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Einbruchschaden? Jetzt Notdienst anrufen!
          </h2>
          <p className="text-xl mb-6">
            Wir sind in 15-30 Minuten bei Ihnen und reparieren Türen & Fenster professionell.
          </p>
          <a 
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-white text-orange-600 px-8 py-4 rounded-full font-bold text-xl hover:bg-gray-100 transition-all shadow-lg"
          >
            <Phone size={28} />
            {displayPhone}
          </a>
          <p className="mt-4 text-white/90">24/7 Notdienst • Schadensdokumentation für Versicherung inklusive</p>
        </section>

        {/* Weitere Leistungen */}
        <section className="mt-12 pt-8 border-t-2 border-gray-200">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">
            Weitere Leistungen unseres Schlüsseldienstes
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Link to="/leistungen/einbruchschutz" className="block bg-gray-50 hover:bg-orange-50 border border-gray-300 hover:border-orange-400 rounded-lg p-4 transition-all">
              <h3 className="font-bold text-gray-900 mb-2">🛡️ Einbruchschutz & Sicherheitsberatung</h3>
              <p className="text-gray-600 text-sm">Schützen Sie Ihr Zuhause präventiv vor Einbrüchen</p>
            </Link>
            <Link to="/leistungen/schlosswechsel" className="block bg-gray-50 hover:bg-orange-50 border border-gray-300 hover:border-orange-400 rounded-lg p-4 transition-all">
              <h3 className="font-bold text-gray-900 mb-2">🔧 Schlosswechsel & Zylindertausch</h3>
              <p className="text-gray-600 text-sm">Neue Schlösser nach Einzug oder Schlüsselverlust</p>
            </Link>
            <Link to="/leistungen/tueroeffnung-verschlossen" className="block bg-gray-50 hover:bg-orange-50 border border-gray-300 hover:border-orange-400 rounded-lg p-4 transition-all">
              <h3 className="font-bold text-gray-900 mb-2">🔐 Türöffnung verschlossen</h3>
              <p className="text-gray-600 text-sm">Hilfe bei Schlüsselverlust oder defektem Schloss</p>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default EinbruchschadenReparaturPage;
